/**
 * Group sessions (ZOY-685/686, Product 2026-09-15) — verified against
 * cms-backend src/group-sessions/{dto,group-sessions.controller,
 * group-sessions.service}.ts at main 3ea3d4c; contract doc
 * docs/api/group-sessions.md.
 *
 * ONE occurrence, several participants — like a calendar event with
 * attendees. The occurrence owns what is shared (service, leading therapist,
 * mode, room, time, capacity, join destination, start/end facts) and
 * reserves the therapist and room once. Every participant is an ordinary
 * booking row under it (`groupSessionId`) that keeps its own package line or
 * ad-hoc price, delivery, outcome, payments and Plan mirror — nothing is
 * shared across attendees except the time and the Group Report. There is no
 * persistent group: the next occurrence has its own attendees.
 */

import { apiRequest } from '../api/http';
import type { BookingView, BookingWarning, JoinKind } from './bookingsApi';
import type { GroupReport } from './sessionsApi';

/** A group occurrence is in clinic (optional room) or online — never off site. */
export const GROUP_SESSION_MODES = ['in_clinic', 'online'] as const;
export type GroupSessionMode = (typeof GROUP_SESSION_MODES)[number];
/** Product caps on live participations per occurrence. */
export const GROUP_CAPACITY_LIMIT: Record<GroupSessionMode, number> = { in_clinic: 20, online: 15 };

export type GroupExecutionStatus = 'scheduled' | 'in_progress' | 'completed' | 'cancelled';

export interface GroupSessionView {
  id: string;
  serviceId: string;
  practitionerUid: string;
  locationType: GroupSessionMode | (string & {});
  roomId: string | null;
  /** The repeat run this occurrence was created in (2026-10-28); null when created alone. */
  seriesId?: string | null;
  startsAt: string;
  endsAt: string;
  capacity: number;
  status: 'scheduled' | 'cancelled';
  statusReason: string | null;
  startedAt: string | null;
  startedBy: string | null;
  endedAt: string | null;
  endedBy: string | null;
  joinKind: JoinKind | null;
  joinUrl: string | null;
  createdBy: string;
  createdAt: string;
  service: { name: string; workflowType: string | null };
  /** ZOY-686: the shared report, null until written. */
  groupReport: GroupReport | null;
  /** Live participations (booked). */
  participantCount: number;
  /** Derived on every read, never stored. */
  executionStatus: GroupExecutionStatus;
}

export interface GroupSessionDetail extends GroupSessionView {
  /** Every participation ever, oldest first — cancelled and removed ones included. */
  participants: BookingView[];
}

export const GroupSessionErrorCode = {
  NotFound: 'GROUP_SESSION_NOT_FOUND',
  NotActive: 'GROUP_SESSION_NOT_ACTIVE',
  AlreadyStarted: 'GROUP_SESSION_ALREADY_STARTED',
  NotStarted: 'GROUP_SESSION_NOT_STARTED',
  AlreadyEnded: 'GROUP_SESSION_ALREADY_ENDED',
  ServiceNotGroup: 'GROUP_SESSION_SERVICE_NOT_GROUP',
  ModeNotAllowed: 'GROUP_SESSION_MODE_NOT_ALLOWED',
  CapacityRequired: 'GROUP_SESSION_CAPACITY_REQUIRED',
  CapacityExceedsLimit: 'GROUP_SESSION_CAPACITY_EXCEEDS_LIMIT',
  Full: 'GROUP_SESSION_FULL',
  PatientAlreadyIn: 'GROUP_SESSION_PATIENT_ALREADY_IN',
  ParticipantNotFound: 'GROUP_SESSION_PARTICIPANT_NOT_FOUND',
  PractitionerOverlap: 'GROUP_SESSION_PRACTITIONER_OVERLAP',
  RoomOverlap: 'GROUP_SESSION_ROOM_OVERLAP',
  ParticipantOverlap: 'GROUP_SESSION_PARTICIPANT_OVERLAP',
  ReportAlreadyWritten: 'GROUP_REPORT_ALREADY_WRITTEN',
  OnlineSessionLocked: 'GROUP_SESSION_ONLINE_SESSION_LOCKED',
} as const;

export interface CreateGroupSessionBody {
  serviceId: string;
  practitionerUid: string;
  locationType: GroupSessionMode;
  roomId?: string;
  /** One client-made UUID per repeat run, the same on every date of it. */
  seriesId?: string;
  startsAt: string;
  endsAt: string;
  /** Omitted = the service's default group capacity. */
  capacity?: number;
  joinKind?: JoinKind;
  joinUrl?: string;
}

export interface RescheduleGroupSessionBody {
  startsAt?: string;
  endsAt?: string;
  practitionerUid?: string;
  /** Explicit null clears the room. */
  roomId?: string | null;
  /** Tri-state, as on a booking. */
  joinKind?: JoinKind | null;
  joinUrl?: string | null;
}

export const createGroupSession = (body: CreateGroupSessionBody) =>
  apiRequest<GroupSessionView>({
    base: 'clinic',
    method: 'POST',
    path: '/v1/group-sessions',
    body,
  });

/** Occurrences starting inside a bounded window (≤ 93 days) — the calendar's group source. */
export const listGroupSessions = (from: Date, to: Date) =>
  apiRequest<{ items: GroupSessionView[] }>({
    base: 'clinic',
    path: '/v1/group-sessions',
    query: { from: from.toISOString(), to: to.toISOString() },
  });

/** Every occurrence of one repeat series (any status), oldest first — no window needed. */
export const listGroupSeries = (seriesId: string) =>
  apiRequest<{ items: GroupSessionView[] }>({
    base: 'clinic',
    path: '/v1/group-sessions',
    query: { seriesId },
  });

export const fetchGroupSession = (id: string) =>
  apiRequest<GroupSessionDetail>({ base: 'clinic', path: `/v1/group-sessions/${id}` });

/** Time, therapist, room and destination — every live participation follows. Not after the start. */
export const rescheduleGroupSession = (id: string, body: RescheduleGroupSessionBody) =>
  apiRequest<GroupSessionView>({
    base: 'clinic',
    method: 'PATCH',
    path: `/v1/group-sessions/${id}`,
    body,
  });

/** An attendee joins this occurrence with their own package line or ad-hoc price. */
export const addGroupParticipant = (
  id: string,
  body: { patientId: string; packageItemId?: string; overScheduleReason?: string },
) =>
  apiRequest<BookingView & { warnings: BookingWarning[] }>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/group-sessions/${id}/participants`,
    body,
  });

/** Before the start only: the attendee leaves (their booking is cancelled, `removed_from_group`). */
export const removeGroupParticipant = (id: string, bookingId: string) =>
  apiRequest<void>({
    base: 'clinic',
    method: 'DELETE',
    path: `/v1/group-sessions/${id}/participants/${bookingId}`,
  });

/** The occurrence and every live participation are cancelled. */
export const cancelGroupSession = (id: string, reason?: string) =>
  apiRequest<GroupSessionView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/group-sessions/${id}/cancel`,
    body: reason ? { reason } : {},
  });

/** The therapist's start act for the whole group; registered participants start too. */
export const startGroupSession = (id: string) =>
  apiRequest<GroupSessionView & { started: string[]; notStarted: string[] }>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/group-sessions/${id}/start`,
    body: {},
  });

/** The occurrence is over; each participation still closes on its own. */
export const endGroupSession = (id: string) =>
  apiRequest<GroupSessionView>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/group-sessions/${id}/end`,
    body: {},
  });

/** ZOY-686: written once per occurrence, after the start; no edit or delete route. */
export const writeGroupReport = (id: string, body: string) =>
  apiRequest<GroupReport>({
    base: 'clinic',
    method: 'POST',
    path: `/v1/group-sessions/${id}/report`,
    body: { body },
  });
